import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT_YYYY_MM_DD, REQUEST_STATUS } from '@shared/constants/otherConstants';
import { useURLParams } from '@shared/hooks/useURLParams';
import useFirstRender from '@shared/hooks/useFirstRender';
import useURLFilters from '@shared/hooks/useURLFilters';
import { usePrevious } from '@shared/hooks/usePrevious';
import { getStateFromUrlParams, toTitleCase } from '@shared/utils/utilsOld';
import { toSnakeCase } from '@shared/utils/utils';

export function useListState(tableName, disableRequestsOnChangeState, getDataAction, params) {
    const [urlParams, setUrlParams] = useURLParams();
    const isFirstRender = useFirstRender();
    const [rowSelection, setRowSelection] = useState({});
    const disableUrlParams = params?.disableUrlParams;
    const [pagination, setPagination] = useState(getStateFromUrlParams('pagination', urlParams, disableUrlParams));
    const { pageIndex, pageSize } = pagination;
    const [globalFilter, setGlobalFilter] = useState(
        getStateFromUrlParams('globalFilter', urlParams, disableUrlParams),
    );
    const [sorting, setSorting] = useState(getStateFromUrlParams('sorting', urlParams, disableUrlParams));
    const [columnFilters, setColumnFilters] = useState(
        getStateFromUrlParams('columnFilters', urlParams, disableUrlParams),
    );
    const [showColumnFilters, setShowColumnFilters] = useState(Boolean(columnFilters.length));
    const prevSorting = usePrevious(sorting);
    const prevGlobalFilter = usePrevious(globalFilter);
    const prevColumnFilters = usePrevious(columnFilters);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const fullModuleName = params?.fullModuleName;
    const createdAtLast = params?.createdAtLast;
    const modules = params?.modules;
    const tags = params?.tags;
    const favoriteUserId = params?.favoriteUserId;
    const deleteStatus = params?.deleteStatus;
    const module = params?.module;
    const branchesFilter = params?.branchesFilter;
    const testrunId = params?.testRunId || params?.testrunId;
    const testplanId = params?.testplanId;
    const testcaseId = params?.testcaseId;
    const latestOnly = params?.latestOnly;
    const enableInfiniteScroll = params?.enableInfiniteScroll;
    const schemeId = params?.schemeId;
    const defectId = params?.defectId;
    const errorMessagePattern = params?.errorMessagePattern;
    const stackTracePattern = params?.stackTracePattern;
    const testresultId = params?.testresultId;

    useEffect(() => {
        if (errorMessagePattern && !isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorMessagePattern]);

    useEffect(() => {
        if (stackTracePattern && !isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stackTracePattern]);

    useEffect(() => {
        if (deleteStatus === REQUEST_STATUS.SUCCESS) {
            fetchData();
            setRowSelection({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteStatus]);

    useEffect(() => {
        !disableRequestsOnChangeState &&
            tableName !== 'testCasesTableForModules' &&
            module === undefined &&
            !enableInfiniteScroll &&
            fetchData('first');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableRequestsOnChangeState]);

    useEffect(() => {
        if (module || module === null) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [module]);

    useEffect(() => {
        if (favoriteUserId) {
            fetchData('favorite');
        } else {
            if (favoriteUserId === null && !isFirstRender) {
                fetchData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favoriteUserId]);

    useEffect(() => {
        if (tags && !isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tags]);

    useEffect(() => {
        if (!isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modules]);

    useEffect(() => {
        if (fullModuleName) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullModuleName]);
    useEffect(() => {
        if (branchesFilter) {
            fetchData('branchesFilter');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchesFilter]);

    useEffect(() => {
        if (createdAtLast) {
            fetchData('createdAtLast');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdAtLast]);

    useEffect(() => {
        if (testrunId && !isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testrunId]);

    useEffect(() => {
        if (testcaseId && !isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testcaseId]);

    useEffect(() => {
        if (showColumnFilters === false) {
            setColumnFilters([]);
        }
    }, [showColumnFilters]);

    useEffect(() => {
        if (columnFilters.length) {
            setShowColumnFilters(true);
        }
    }, [columnFilters.length]);

    // handle ordering changes
    useEffect(() => {
        if (prevSorting && !isEqual(prevSorting, sorting)) {
            fetchData('ordering');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting, prevSorting]);

    // handle pageIndex & pageSize changes
    useEffect(() => {
        if (!isFirstRender) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageIndex, pageSize]);

    useURLFilters({
        urlParams,
        setUrlParams,
        params: {
            globalFilter,
            sorting,
            columnFilters,
            tags,
            pageIndex,
            pageSize,
            module,
            modules,
        },
        disableFilters: isFirstRender || disableRequestsOnChangeState || disableUrlParams,
    });

    const renameSortingId = useCallback((id, tableName) => {
        let newName = id;
        switch (id) {
            case 'module': {
                newName = 'module_id__name';
                break;
            }
            case 'testcases': {
                newName = 'testcases_cnt';
                break;
            }
            case 'test_runs': {
                newName = 'testruns_cnt';
                break;
            }
            case 'created_by_user': {
                newName = tableName === 'testRunsTable' ? 'created_by_user__username' : 'created_by_user';
                break;
            }
            case 'testResultsCount':
            case 'testCasesCount':
            case 'testRunsCount': {
                newName = toSnakeCase(id);
                break;
            }
            default:
                break;
        }
        return newName;
    }, []);

    const getTransformedColumnFilters = useCallback(
        (columnFilters, tableName) => {
            return columnFilters.reduce((result, { id, value }) => {
                switch (id) {
                    case 'created_by_user':
                    case 'assigned_user':
                        return { ...result, [id]: value };
                    case 'created_at':
                    case 'last_testrun':
                    case 'createdAt':
                    case 'start_time':
                    case 'expires_at': {
                        return {
                            ...result,
                            [id]: dayjs(value).format(DATE_FORMAT_YYYY_MM_DD),
                        };
                    }
                    case 'start_date':
                    case 'end_date': {
                        return {
                            ...result,
                            [id]: dayjs(value).format(DATE_FORMAT_YYYY_MM_DD),
                        };
                    }
                    case 'pk': {
                        return {
                            ...result,
                            ...(tableName === 'schemesTable' || tableName === 'defectsTable'
                                ? { id: value }
                                : tableName === 'standsTable'
                                  ? { id: value }
                                  : { id__istartswith: value }),
                        };
                    }
                    case 'module':
                        return {
                            ...result,
                            module__path__icontains: value,
                        };
                    case 'type': {
                        return tableName === 'testCasesTableForModules' || tableName === 'testCasesTable'
                            ? {
                                  ...result,
                                  is_handtest: value === t('hand-test'),
                              }
                            : {
                                  ...result,
                                  type: value,
                              };
                    }
                    case 'id':
                    case 'objective':
                    case 'modules':
                    case 'tags':
                    case 'team':
                    case 'stands': {
                        return {
                            ...result,
                            [id]: value,
                        };
                    }
                    case 'title':
                    case 'build':
                        return tableName === 'testrunResultsTable' ||
                            tableName === 'testRunsTable' ||
                            tableName === 'standsTable'
                            ? {
                                  ...result,
                                  [id]: value,
                              }
                            : {
                                  ...result,
                                  [id + '__icontains']: value,
                              };
                    case 'branch':
                        return tableName !== 'testcaseRunHistoryTable'
                            ? {
                                  ...result,
                                  [id]: value,
                              }
                            : {
                                  ...result,
                                  [id + '__icontains']: value,
                              };
                    case 'access':
                        return {
                            ...result,
                            access: toTitleCase(String(value)),
                        };
                    case 'testcases_cnt':
                        return {
                            ...result,
                            testcases_cnt: value === 'true',
                        };
                    case 'scheme': {
                        return {
                            ...result,
                            scheme: value,
                        };
                    }
                    case 'jiraIssueKey': {
                        return {
                            [toSnakeCase(id)]: value,
                        };
                    }
                    case 'scheme__icontains':
                    case 'status': {
                        return {
                            ...result,
                            status: value,
                        };
                    }
                    case 'scheme_name': {
                        return {
                            ...result,
                            scheme_name: value,
                        };
                    }
                    case 'scheme_id': {
                        return {
                            ...result,
                            scheme_id: value,
                        };
                    }
                    case 'testrun': {
                        return {
                            ...result,
                            testrun: value,
                        };
                    }
                    case 'vms_in_stand': {
                        return {
                            ...result,
                            vms: value,
                        };
                    }
                    default: {
                        return {
                            ...result,
                            [`${id}${tableName !== 'testRunsTable' && tableName !== 'testrunResultsTable' && tableName !== 'testcaseRunHistoryTable' && tableName !== 'defectsTable' ? '__icontains' : ''}`]:
                                value,
                        };
                    }
                }
            }, {});
        },
        [t],
    );

    const fetchData = useCallback(
        (fetchReason) => {
            const transformedColumnFilters = getTransformedColumnFilters(columnFilters, tableName);
            if (
                fetchReason === 'ordering' ||
                fetchReason === 'filter' ||
                fetchReason === 'duplicate' ||
                fetchReason === 'createdAtLast' ||
                fetchReason === 'refresh button' ||
                fetchReason === 'favorite' ||
                fetchReason === 'branchesFilter' ||
                fetchReason === 'add testcases'
            ) {
                if (isFirstRender) {
                    return;
                }

                if (pageIndex > 0) {
                    setPagination((prev) => {
                        return {
                            ...prev,
                            pageIndex: 0,
                        };
                    });
                    return;
                }
            }
            if (disableRequestsOnChangeState) {
                return;
            }

            dispatch(
                getDataAction({
                    page_index: pageIndex,
                    page_size: pageSize,
                    search: globalFilter?.length ? globalFilter : undefined,
                    ordering: sorting.length
                        ? `${sorting[0].desc ? '-' : ''}${renameSortingId(sorting[0].id, tableName)}`
                        : undefined,
                    module: fullModuleName !== 'all-modules' ? fullModuleName || module : undefined,
                    createdAtLast: (createdAtLast !== 'all' && createdAtLast) || undefined,
                    modules: modules?.length ? '[' + modules.join(', ') + ']' : undefined,
                    tags: tags?.length ? '[' + tags.join(', ') + ']' : undefined,
                    favorite_user_id: favoriteUserId,
                    filter: branchesFilter !== 'all' ? branchesFilter : undefined,
                    testrun: testrunId,
                    testplan: testplanId,
                    testcase: testcaseId,
                    scheme: schemeId,
                    latest_only: latestOnly,
                    enableInfiniteScroll,
                    defect: defectId,
                    error_message: errorMessagePattern,
                    stack_trace: stackTracePattern,
                    testresult: testresultId,
                    ...transformedColumnFilters,
                }),
            );
        },
        [
            branchesFilter,
            columnFilters,
            createdAtLast,
            defectId,
            disableRequestsOnChangeState,
            dispatch,
            enableInfiniteScroll,
            errorMessagePattern,
            favoriteUserId,
            fullModuleName,
            getDataAction,
            getTransformedColumnFilters,
            globalFilter,
            isFirstRender,
            latestOnly,
            module,
            modules,
            pageIndex,
            pageSize,
            renameSortingId,
            schemeId,
            sorting,
            stackTracePattern,
            tableName,
            tags,
            testcaseId,
            testplanId,
            testresultId,
            testrunId,
        ],
    );

    const updatedSetPagination = useCallback(
        (updater) => {
            if (isFirstRender) {
                return;
            }
            setPagination(updater);
        },
        [isFirstRender],
    );

    // handle globalFilter & columnFilters changes
    useEffect(() => {
        if (
            (globalFilter !== '' && globalFilter !== prevGlobalFilter) || // global filter changed
            (globalFilter === '' && prevGlobalFilter !== undefined && globalFilter !== prevGlobalFilter) || // global filter was set to empty string
            (columnFilters.length && !isEqual(columnFilters, prevColumnFilters)) || // column filter enabled
            (!columnFilters.length && !isEqual(columnFilters, prevColumnFilters) && prevColumnFilters !== undefined)
        ) {
            fetchData('filter');
        }
    }, [globalFilter, columnFilters, prevGlobalFilter, prevColumnFilters, fetchData]);

    return {
        rowSelection,
        setRowSelection,
        pagination,
        setPagination: updatedSetPagination,
        globalFilter,
        setGlobalFilter,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        fetchData,
    };
}
